import { z } from 'zod';

/**
 * Options for configuring a tool
 */
export interface ToolOptions {
  /** The name of the tool */
  name: string;
  /** Zod schema for input validation */
  input: z.ZodSchema;
  /** Description of what the tool does */
  description: string;
  /** Whether the tool is deprecated */
  deprecated?: boolean;
  /** Additional metadata for the tool */
  metadata?: Record<string, unknown>;
}

/**
 * Options for configuring a resource
 */
export interface ResourceOptions {
  /** The name of the resource */
  name: string;
  /** Description of the resource */
  description: string;
  /** MIME type of the resource */
  mimeType?: string;
  /** Whether the resource is deprecated */
  deprecated?: boolean;
  /** Additional metadata for the resource */
  metadata?: Record<string, unknown>;
}

/**
 * Context object passed to tool and resource handlers
 */
export interface BeastMcpContext {
  /** Log a message */
  log(message: string): void;
  /** Report progress (0-100) */
  reportProgress(percent: number, message?: string): void;
  /** Get the current tool/resource ID */
  getToolId(): string;
  /** Get the current session ID */
  getSessionId(): string;
  /** Get the host ID */
  getHostId(): string;
  /** Performance monitoring hooks */
  startTimer(name: string): void;
  endTimer(name: string): number;
  getTimers(): Record<string, number>;
  recordMetric(name: string, value: number): void;
  getMetrics(): Record<string, number[]>;
}

/**
 * Standard error types for BeastMCP
 */
export class ValidationError extends Error {
  constructor(message: string, public readonly details?: unknown) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class ToolExecutionError extends Error {
  constructor(message: string, public readonly toolId?: string) {
    super(message);
    this.name = 'ToolExecutionError';
  }
}

export class ResourceError extends Error {
  constructor(message: string, public readonly resourceId?: string) {
    super(message);
    this.name = 'ResourceError';
  }
}

/**
 * Registry for managing tool and resource metadata
 */
export interface ToolMetadata {
  id: string;
  options: ToolOptions;
  handler: (args: any, context: any) => Promise<any>;
}

export interface ResourceMetadata {
  id: string;
  options: ResourceOptions;
  handler: (context: any) => Promise<any>;
}

/**
 * Progress reporting interface
 */
export interface ProgressUpdate {
  percent: number;
  message?: string;
  timestamp: number;
} 